/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useMemo } from 'react';
import { View, Dimensions, ActivityIndicator } from 'react-native';
import { pd } from '@airasia-rn/ui';
import { FeedItemView } from '../../components/FeedItemView';
import { StyleSheet } from 'react-native-unistyles';
import type { Post, RecommendationApiItem } from '../../services/types';

const HORIZONTAL_GAP = 2; // Matches typical IG/TikTok grid
const GRID_PADDING_HORIZONTAL = 16; // Grid has 16 padding on left and right
const NUM_COLUMNS = 3;

export const FeedListWidget = ({
  items = [],
  onFeedItemClick,
  isLoadingMore,
}: {
  items?: RecommendationApiItem[];
  onFeedItemClick?: (post: Post, index: number) => void;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  hasNextPage?: boolean;
}) => {
  const screenWidth = Dimensions.get('window').width;

  const availableWidth = screenWidth - GRID_PADDING_HORIZONTAL * 2;
  const totalGap = (NUM_COLUMNS - 1) * HORIZONTAL_GAP;
  const itemWidth = Math.floor((availableWidth - totalGap) / NUM_COLUMNS);

  const posts = useMemo(() => {
    return items.map((item, index) => ({
      post_id: item.itemId || item.lobItemId || `post_${index}`,
      rank: index + 1,
      media: {
        type: (item.video ? 'VIDEO' : 'IMAGE') as 'VIDEO' | 'IMAGE',
        thumbnail: {
          url: item.image || '',
          width: 360,
          height: 640,
        },
        duration_ms: item.video ? 15000 : 0,
      },
      snippet: item.title || '',
      deeplink: item.deepLink || '',
    }));
  }, [items]);

  // Render the grid as inline, content-sized rows rather than a scrolling
  // FlatList. This widget is always embedded inside the Home page's outer
  // scroll list, where a flex:1 VirtualizedList collapses to ~0 height (and
  // nests VirtualizedLists inside a ScrollView). A plain wrapped grid grows
  // with its content and scrolls with the page — matching the For You feed.
  return (
    <View style={styles.container}>
      <View style={styles.grid}>
        {posts.map((post, index) => {
          const isFirstColumn = index % NUM_COLUMNS === 0;
          const isLastColumn = index % NUM_COLUMNS === NUM_COLUMNS - 1;
          const isFirstRow = index < NUM_COLUMNS;
          const isLastRow =
            index >= posts.length - (posts.length % NUM_COLUMNS || NUM_COLUMNS);
          return (
            <View
              key={post.post_id}
              style={[
                styles.gridItem,
                {
                  marginRight: isLastColumn ? 0 : HORIZONTAL_GAP,
                  // Row-gap emulation: RN doesn't collapse trailing margins,
                  // so skip marginBottom on the last row to keep the grid's
                  // bottom edge aligned with the skeleton's.
                  marginBottom: isLastRow ? 0 : HORIZONTAL_GAP,
                },
              ]}
            >
              <FeedItemView
                post={post}
                onPress={() => onFeedItemClick?.(post, index)}
                itemWidth={itemWidth}
                isTopLeft={isFirstRow && isFirstColumn}
                isTopRight={isFirstRow && isLastColumn}
                isBottomLeft={isLastRow && isFirstColumn}
                isBottomRight={
                  (isLastRow && isLastColumn) || index === posts.length - 1
                }
                disableShimmer={false}
                sectionId="movements"
              />
            </View>
          );
        })}
      </View>
      {isLoadingMore && (
        <View style={styles.loaderContainer}>
          <ActivityIndicator
            size="small"
            color={pd.sys.colour.interactive.toString()}
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create(() => ({
  container: {
    paddingHorizontal: GRID_PADDING_HORIZONTAL,
    // No paddingBottom — matches MovementsGridSkeletonView's grid container,
    // which also has none, so loaded and skeleton bottom edges stay aligned.
  },
  grid: {
    flexDirection: 'row' as const,
    flexWrap: 'wrap' as const,
  },
  gridItem: {
    // marginBottom is set per-item inline (skipped on the last row) — see render.
  },
  loaderContainer: {
    paddingVertical: 24,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    paddingBottom: 50,
  },
}));
